import React, { useMemo, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack
} from '@chakra-ui/react'
import { useMutation } from 'urql'

import { Upup_UpdateRentalAddOnDocument as UpdateRentalAddOn } from 'graphql/generated'
import type { RentRollAddOn } from '../index'
import RentalAddOnScheduleFields from '../RentalAddOnScheduleFields'
import {
  formatDateForInput,
  getRentalAddOnFormErrors,
  hasRentalAddOnFormErrors
} from '../validation'

type EditRentalAddOnFormProps = {
  addOn: RentRollAddOn
  close: () => void
  onSuccess?: () => void
  occupancyDate: string
}

export default function EditRentalAddOnForm({
  addOn,
  close,
  onSuccess,
  occupancyDate
}: EditRentalAddOnFormProps) {
  const [originalAddOn] = useState(() => ({ ...addOn }))
  const [staleError, setStaleError] = useState('')
  const initialAmount = String(originalAddOn.price)
  const initialStartDate = formatDateForInput(originalAddOn.start_date)
  const initialEndDate = formatDateForInput(originalAddOn.end_date)
  const [amount, setAmount] = useState(initialAmount)
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [isSaving, setIsSaving] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [{ fetching, error }, updateRentalAddOn] = useMutation(UpdateRentalAddOn)

  const errors = getRentalAddOnFormErrors({
    amount,
    endDate,
    occupancyDate,
    startDate
  })
  const isDirty =
    amount !== initialAmount || startDate !== initialStartDate || endDate !== initialEndDate
  const showErrors = hasAttemptedSubmit || isDirty
  const canSubmit =
    isDirty && !hasRentalAddOnFormErrors(errors) && !fetching && !isSaving && !staleError
  const addOnName = originalAddOn.liability_type.name

  const formattedError = useMemo(() => staleError || error?.message, [error, staleError])

  const submitRentalAddOn = async () => {
    setHasAttemptedSubmit(true)

    if (!canSubmit) {
      return
    }

    setIsSaving(true)
    const result = await updateRentalAddOn({
      end_date: endDate || null,
      where: {
        id: { _eq: originalAddOn.id },
        updated_at:
          originalAddOn.updated_at === null || originalAddOn.updated_at === undefined
            ? { _is_null: true }
            : { _eq: originalAddOn.updated_at }
      },
      price: Number(amount),
      start_date: startDate
    })
    setIsSaving(false)

    if (!result.error && result.data?.update_rental_add_ons?.affected_rows !== 1) {
      setStaleError(
        'This add-on changed while the editor was open. Close this form, refresh the add-ons, and reopen it before saving.'
      )
      onSuccess?.()
      return
    }

    if (!result.error) {
      close()
      onSuccess?.()
    }
  }

  return (
    <Stack spacing={5}>
      {formattedError && (
        <Alert status='error'>
          <AlertIcon />
          {formattedError}
        </Alert>
      )}

      <FormControl isDisabled>
        <FormLabel>Add-on type</FormLabel>
        <Input value={addOnName} readOnly />
      </FormControl>

      <RentalAddOnScheduleFields
        amount={amount}
        endDate={endDate}
        errors={errors}
        isDisabled={fetching || isSaving}
        onAmountChange={setAmount}
        onEndDateChange={setEndDate}
        onStartDateChange={setStartDate}
        showErrors={showErrors}
        startDate={startDate}
      />

      <Box display='flex' justifyContent='flex-end' gap={3}>
        <Button variant='outline' onClick={close} isDisabled={fetching || isSaving}>
          Cancel
        </Button>
        <Button
          colorScheme='teal'
          onClick={submitRentalAddOn}
          isDisabled={!canSubmit}
          isLoading={fetching || isSaving}
        >
          Save Add-On
        </Button>
      </Box>
    </Stack>
  )
}
